import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { usePressFeedbackClick } from '../hooks/usePressFeedbackClick.js';
import { focusFirstFocusable, getFocusableElements } from '../utils/focusTrap.js';
import { cx } from '../utils/cx.js';
import { isTopOverlay, popOverlay, pushOverlay } from '../utils/overlayEscStack.js';
import { usePresence } from '../utils/usePresence.js';
import { overlayPresenceProps } from '../hooks/mistRail.js';
import styles from './Popover.module.css';

/** Enter uses base duration; exit is snappier (matches CSS). */
const POPOVER_MS = 160;

function PopoverFallbackTarget({
  onActivate,
  children,
  pressFeedback,
}: {
  onActivate: () => void;
  children: ReactNode;
  pressFeedback: boolean;
}) {
  const activate = () => onActivate();
  const { onClick: pressOnClick, pressAttrs } = usePressFeedbackClick(
    pressFeedback ? () => activate() : undefined,
  );
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        if (pressFeedback) pressOnClick(e);
        else activate();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onActivate();
      }}
      role="button"
      tabIndex={0}
      {...(pressFeedback ? pressAttrs : {})}
    >
      {children}
    </span>
  );
}

function PopoverDomTarget({
  element,
  opened,
  modal,
  panelId,
  toggleOnTargetClick,
  toggle,
  pressFeedback,
}: {
  element: ReactElement<{
    onClick?: (e: ReactMouseEvent) => void;
    'aria-expanded'?: boolean;
    'aria-haspopup'?: string | boolean;
    'aria-controls'?: string;
  }>;
  opened: boolean;
  modal?: boolean;
  panelId?: string;
  toggleOnTargetClick: boolean;
  toggle: () => void;
  pressFeedback: boolean;
}) {
  const origOnClick = element.props.onClick;
  const run = (e: ReactMouseEvent) => {
    origOnClick?.(e);
    e.stopPropagation();
    if (toggleOnTargetClick) toggle();
  };
  const { onClick: pressOnClick, pressAttrs } = usePressFeedbackClick(
    pressFeedback ? run : undefined,
  );
  return cloneElement(element, {
    onClick: pressFeedback ? pressOnClick : run,
    ...(pressFeedback ? pressAttrs : {}),
    'aria-expanded': opened,
    ...(modal
      ? {
          'aria-haspopup': 'dialog' as const,
          ...(panelId && opened ? { 'aria-controls': panelId } : {}),
        }
      : {}),
  });
}

export type PopoverProps = {
  opened: boolean;
  onChange: (open: boolean) => void;
  target: ReactNode;
  children: ReactNode;
  /**
   * Dialog semantics + focus trap (use with `portal` for large panels).
   * Requires `panelId` and `ariaLabelledBy` for accessible naming.
   */
  modal?: boolean;
  /** `id` on the panel element (required when `modal`). */
  panelId?: string;
  /** id of the visible title element (`aria-labelledby`). */
  ariaLabelledBy?: string;
  /**
   * Render panel in `document.body` with fixed position (avoids overflow clipping in scroll containers).
   * Recomputes position on scroll/resize while open.
   */
  portal?: boolean;
  /** Horizontal alignment of the panel relative to the anchor (portal mode). */
  align?: 'start' | 'end';
  /** Min width for portal panel (default 200). */
  portalMinWidth?: number;
  /** Max width for portal panel (default 320). Raise for wide panels (e.g. wizards). */
  portalMaxWidth?: number;
  /** Estimated panel height for auto vertical placement in portal mode. */
  portalEstimatedHeight?: number;
  /** Gap between anchor and panel in portal mode (px). Default 6. */
  portalGap?: number;
  /** Optional z-index override for portal panel. */
  portalZIndex?: number | string;
  /** When false, target click does not toggle (controlled `opened` only). Default true. */
  toggleOnTargetClick?: boolean;
  /**
   * When false, target click runs immediately (no press-hold delay).
   * Prefer false for form selects / filters; default true for chrome.
   */
  pressFeedback?: boolean;
  /** Stretch the anchor to 100% of its parent (combobox-style triggers). */
  fullWidth?: boolean;
};

export function Popover({
  opened,
  onChange,
  target,
  children,
  portal = false,
  align = 'start',
  portalMinWidth = 200,
  portalMaxWidth = 320,
  portalEstimatedHeight = 280,
  portalGap = 6,
  portalZIndex,
  toggleOnTargetClick = true,
  pressFeedback = true,
  fullWidth = false,
  modal = false,
  panelId,
  ariaLabelledBy,
}: PopoverProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  /** Stable dismiss callback — effect must not re-push overlay when parent passes a new `onChange` each render (nested Select would lose top-of-stack). */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [portalPos, setPortalPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const { mounted, visible } = usePresence(opened, { ms: POPOVER_MS, panelRef });
  const resolvedPortalZIndex =
    portalZIndex ?? (modal && portal ? 'var(--wp-z-popover-over-modal)' : undefined);

  const updatePortalPos = useCallback(
    (measuredPanelHeight?: number) => {
      const el = anchorRef.current;
      if (!el || !portal) return;
      const VIEW = 8;
      const vw = window.innerWidth;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const r = el.getBoundingClientRect();
      const width = Math.min(
        portalMaxWidth,
        Math.max(portalMinWidth, Math.min(vw - VIEW * 2, portalMaxWidth)),
      );
      const gap = Math.max(0, portalGap);
      const naturalH = Math.max(
        1,
        measuredPanelHeight ??
          panelRef.current?.getBoundingClientRect().height ??
          portalEstimatedHeight,
      );
      const anchorMissing = r.width < 2 && r.height < 2;
      let left: number;
      let top: number;
      let maxHeight: number;

      if (anchorMissing) {
        maxHeight = Math.max(120, vh - VIEW * 2);
        const panelH = Math.min(naturalH, maxHeight);
        left = Math.max(VIEW, (vw - width) / 2);
        top = Math.max(VIEW, (vh - panelH) / 2);
      } else {
        left = align === 'end' ? r.right - width : r.left;
        left = Math.max(VIEW, Math.min(left, vw - width - VIEW));

        const spaceBelow = Math.max(0, vh - r.bottom - VIEW - gap);
        const spaceAbove = Math.max(0, r.top - VIEW - gap);
        const fitsBelow = spaceBelow >= naturalH;
        const fitsAbove = spaceAbove >= naturalH;
        let placeBelow: boolean;
        if (fitsBelow && fitsAbove) {
          /* Prefer above when tied — dock chips sit on the bottom edge. */
          placeBelow = spaceBelow > spaceAbove;
        } else if (fitsAbove) {
          placeBelow = false;
        } else if (fitsBelow) {
          placeBelow = true;
        } else {
          placeBelow = spaceBelow >= spaceAbove;
        }

        const available = placeBelow ? spaceBelow : spaceAbove;
        maxHeight = Math.max(120, available > 0 ? available : vh - VIEW * 2);
        const panelH = Math.min(naturalH, maxHeight);

        if (placeBelow) {
          top = r.bottom + gap;
          if (top + panelH > vh - VIEW) {
            maxHeight = Math.max(120, vh - VIEW - top);
          }
        } else {
          /* Pin bottom of panel just above the trigger (dock-safe). */
          top = r.top - gap - panelH;
          if (top < VIEW) {
            top = VIEW;
            maxHeight = Math.max(120, r.top - gap - VIEW);
          }
        }

        maxHeight = Math.max(120, Math.min(maxHeight, vh - top - VIEW));
      }

      setPortalPos((prev) => {
        const next = { top, left, width, maxHeight };
        if (
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.maxHeight === next.maxHeight
        ) {
          return prev;
        }
        return next;
      });
    },
    [portal, align, portalMinWidth, portalMaxWidth, portalEstimatedHeight, portalGap],
  );

  // Keyed on `mounted`, not `opened`, so the panel keeps its position while it animates out.
  useLayoutEffect(() => {
    if (!mounted || !portal) {
      setPortalPos(null);
      return;
    }
    updatePortalPos();
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const h = Math.ceil(Math.max(panel.scrollHeight, panel.getBoundingClientRect().height));
      if (h > 0) updatePortalPos(h);
    });
    return () => cancelAnimationFrame(raf);
  }, [mounted, portal, updatePortalPos, children]);

  /* StepSwitch / async content grow — keep panel inside the viewport. */
  useEffect(() => {
    if (!opened || !portal) return;
    const panel = panelRef.current;
    if (!panel || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      /* scrollHeight = natural content; client height is already maxHeight-capped. */
      const h = Math.ceil(Math.max(panel.scrollHeight, panel.getBoundingClientRect().height));
      if (h > 0) updatePortalPos(h);
    });
    ro.observe(panel);
    return () => ro.disconnect();
  }, [opened, portal, updatePortalPos, children]);

  useEffect(() => {
    if (!opened || !portal) return;
    const onMove = () => updatePortalPos();
    window.addEventListener('resize', onMove);
    window.visualViewport?.addEventListener('resize', onMove);
    window.visualViewport?.addEventListener('scroll', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.visualViewport?.removeEventListener('resize', onMove);
      window.visualViewport?.removeEventListener('scroll', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [opened, portal, updatePortalPos]);

  useEffect(() => {
    if (!opened) return;
    const stackId = pushOverlay();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Shared stack: a popover opened over a modal/popover closes first, and only it.
      if (!isTopOverlay(stackId)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onChangeRef.current(false);
    };
    /** Capture: run before nested panels `stopPropagation`, so outside dismiss works. */
    const onPointerDownCapture = (e: PointerEvent) => {
      if (!isTopOverlay(stackId)) return;
      const t = e.target as Node | null;
      if (!t) return;
      if (anchorRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      onChangeRef.current(false);
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      popOverlay(stackId);
    };
  }, [opened]);

  useEffect(() => {
    if (!opened || !modal) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (panel) focusFirstFocusable(panel);
    });
    return () => {
      cancelAnimationFrame(raf);
      returnFocusRef.current?.focus?.();
      returnFocusRef.current = null;
    };
  }, [opened, modal]);

  /** Modal portal: stop background page scroll stealing the wheel. */
  useEffect(() => {
    if (!opened || !modal || !portal) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, [opened, modal, portal]);

  useEffect(() => {
    if (!opened || !modal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel?.contains(document.activeElement)) return;
      const focusables = getFocusableElements(panel);
      if (focusables.length < 2) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [opened, modal]);

  const toggle = () => onChange(!opened);

  const wrapTarget = (): ReactNode => {
    if (isValidElement(target)) {
      const el = target as ReactElement<{
        onClick?: (e: ReactMouseEvent) => void;
        'aria-expanded'?: boolean;
        'aria-haspopup'?: string | boolean;
        'aria-controls'?: string;
      }>;
      // DOM nodes get press visual hold here unless `pressFeedback={false}`.
      // Button/IconButton/Pressable own their hold when pressFeedback stays on.
      if (typeof el.type === 'string') {
        return (
          <PopoverDomTarget
            element={el}
            opened={opened}
            modal={modal}
            panelId={panelId}
            toggleOnTargetClick={toggleOnTargetClick}
            toggle={toggle}
            pressFeedback={pressFeedback}
          />
        );
      }
      return cloneElement(el, {
        onClick: (e: ReactMouseEvent) => {
          el.props.onClick?.(e);
          e.stopPropagation();
          if (toggleOnTargetClick) toggle();
        },
        'aria-expanded': opened,
        ...(modal
          ? {
              'aria-haspopup': 'dialog' as const,
              ...(panelId && opened ? { 'aria-controls': panelId } : {}),
            }
          : {}),
      });
    }
    return (
      <PopoverFallbackTarget
        pressFeedback={pressFeedback}
        onActivate={() => toggleOnTargetClick && toggle()}
      >
        {target}
      </PopoverFallbackTarget>
    );
  };

  const panelClass = cx(
    portal ? styles.panelPortal : styles.panel,
    modal && portal ? styles.panelPortalModal : null,
    visible ? styles.panelVisible : null,
  );

  const panelInner = (
    <div
      ref={panelRef}
      id={panelId}
      role={modal ? 'dialog' : undefined}
      aria-modal={modal ? true : undefined}
      aria-labelledby={modal && ariaLabelledBy ? ariaLabelledBy : undefined}
      className={panelClass}
      tabIndex={modal ? -1 : undefined}
      {...overlayPresenceProps(visible)}
      style={
        portal && portalPos
          ? {
              top: portalPos.top,
              left: portalPos.left,
              width: portalPos.width,
              maxHeight: portalPos.maxHeight,
              overflow: 'auto',
              overscrollBehavior: 'contain',
              zIndex: resolvedPortalZIndex,
            }
          : undefined
      }
      onMouseDown={(e) => e.stopPropagation()}
      data-wp-popover-panel=""
    >
      {children}
    </div>
  );

  return (
    <div
      className={cx(styles.anchor, fullWidth && styles.anchorFull)}
      ref={anchorRef}
      data-datatable-rowclick-ignore
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {wrapTarget()}
      {mounted && !portal ? panelInner : null}
      {mounted && portal && portalPos
        ? createPortal(panelInner, document.body)
        : null}
    </div>
  );
}
